import React, { type FC, useEffect, useMemo, useState } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import type { DateRange } from 'react-day-picker';
import { useStores } from '../hooks/useStores';
import { useAnalytics } from '../hooks/useAnalytics';
import type { AnalyticsResponse } from '../services/analyticsService';

// ── Types ──────────────────────────────────────────────────────────────────────

type KpiItem  = { label: string; value: string; delta?: string };
type StatusRow = { label: string; value: number };
type SalesPoint = { date: string; orders: number; revenue: number };
type TopProductRow = { sku: string; name: string; units_sold: number; revenue: string; price_buckets: { price: number; units_sold: number }[] };
type TopCategoryRow = { category_id: string; name: string; units_sold: number; revenue: string };

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number) => {
    try {
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    } catch { return `R ${amount.toFixed(2)}`; }
};

const formatDelta = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0.0%';
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const computeRange = (range: DateRange | undefined): { from: string; to: string } | null => {
    if (!range?.from) return null;
    const { from } = range;
    const to = range.to || range.from;
    return {
        from: new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0)).toISOString(),
        to:   new Date(Date.UTC(to.getFullYear(),   to.getMonth(),   to.getDate(),   23, 59, 59, 999)).toISOString(),
    };
};

const getStatusBarColor = (status: string) => {
    const s = status.toLowerCase().replace(/_/g, ' ');
    if (s.includes('delivered') || s.includes('complete')) return 'bg-[#8dc63f]';
    if (s.includes('cancelled')) return 'bg-rose-500';
    if (s.includes('failed'))    return 'bg-[#f5ab40]';
    if (s.includes('packing'))   return 'bg-indigo-500';
    if (s.includes('payment'))   return 'bg-amber-500';
    if (s.includes('route'))     return 'bg-sky-500';
    return 'bg-stone-300';
};

// ── StatCard ───────────────────────────────────────────────────────────────────

const StatCard: FC<{ label: string; value: string; delta?: string; comparisonLabel?: string }> = ({
                                                                                                      label, value, delta, comparisonLabel = 'vs previous period',
                                                                                                  }) => {
    const isNegative = !!delta?.startsWith('-');
    const isPositive = !!delta && !isNegative && delta !== '0.0%' && delta !== '+0.0%';
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium text-stone-500">{label}</p>
            <div className="mt-2 flex items-end justify-between gap-3">
                <p className="text-2xl font-semibold text-stone-900">{value}</p>
                {delta && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        isNegative ? 'bg-rose-50 text-rose-700 ring-rose-200'
                            : isPositive ? 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]'
                                : 'bg-stone-50 text-stone-700 ring-stone-200'
                    }`}>
                        <span aria-hidden="true">{isNegative ? '↓' : isPositive ? '↑' : '→'}</span>
                        <span>{delta}</span>
                    </span>
                )}
            </div>
            <div className="mt-2 min-h-[18px] text-[11px] text-stone-400">{delta ? comparisonLabel : ''}</div>
        </div>
    );
};

// ── SalesChart ─────────────────────────────────────────────────────────────────

const SalesChart: FC<{ data: SalesPoint[]; loading?: boolean }> = ({ data, loading = false }) => {
    const chart = useMemo(() => {
        if (!data.length) return null;
        const width = 800; const height = 260;
        const pad   = { top: 20, right: 20, bottom: 40, left: 50 };
        const iw    = width  - pad.left - pad.right;
        const ih    = height - pad.top  - pad.bottom;
        const maxRev = Math.max(...data.map(d => Number(d.revenue) || 0), 1);

        const points = data.map((pt, i) => ({
            ...pt,
            x: data.length === 1 ? pad.left + iw / 2 : pad.left + (i / (data.length - 1)) * iw,
            y: pad.top + ih - ((Number(pt.revenue) || 0) / maxRev) * ih,
            label: new Date(pt.date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }),
        }));

        const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
        const yTicks = 4;
        const gridLines = Array.from({ length: yTicks + 1 }, (_, i) => {
            const value = (maxRev / yTicks) * i;
            return { value, y: pad.top + ih - (value / maxRev) * ih };
        });
        return { width, height, padding: pad, points, path, gridLines };
    }, [data]);

    const formatCompact = (amount: number) => {
        try { return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', notation: 'compact', maximumFractionDigits: 1 }).format(amount); }
        catch { return `R ${amount.toFixed(2)}`; }
    };

    if (loading) return <div className="h-64 rounded-xl border border-stone-200 bg-stone-50 animate-pulse" />;
    if (!chart)  return (
        <div className="flex h-64 items-center justify-center rounded-xl border-2 border-dashed border-stone-300 text-sm text-stone-400">
            No sales data for this period
        </div>
    );

    return (
        <div className="w-full">
            <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="h-64 w-full">
                {chart.gridLines.map((tick, i) => (
                    <g key={i}>
                        <line x1={chart.padding.left} x2={chart.width - chart.padding.right} y1={tick.y} y2={tick.y} stroke="#e7e5e4" strokeWidth="1" />
                        <text x={chart.padding.left - 10} y={tick.y + 4} textAnchor="end" fontSize="10" fill="#78716c">{formatCompact(tick.value)}</text>
                    </g>
                ))}
                <path d={chart.path} fill="none" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                {chart.points.map(pt => (
                    <g key={pt.date}>
                        <circle cx={pt.x} cy={pt.y} r="4" fill="#0f172a" />
                        <text x={pt.x} y={chart.height - 12} textAnchor="middle" fontSize="10" fill="#78716c">{pt.label}</text>
                    </g>
                ))}
            </svg>
            <div className="mt-3 flex items-center justify-between text-xs text-stone-500">
                <span>Revenue trend (Completed Only)</span>
                <span>{chart.points.length} data points</span>
            </div>
        </div>
    );
};

// ── Analytics page ─────────────────────────────────────────────────────────────

const Analytics: FC = () => {
    const { stores, fetchStores } = useStores();
    const { fetchAnalytics, loading, error } = useAnalytics();

    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: new Date(new Date().setDate(new Date().getDate() - 29)),
        to:   new Date(),
    });
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [expandedRows,    setExpandedRows]     = useState<Set<number>>(new Set());

    const [kpis,           setKpis]           = useState<KpiItem[]>([
        { label: 'Total Orders',          value: '–' },
        { label: 'Revenue',               value: '–' },
        { label: 'Product Revenue',       value: '–' },
        { label: 'Delivery Fee Revenue',  value: '–' },
        { label: 'Avg Order Value',       value: '–' },
        { label: 'Conversion Rate',       value: '–' },
        { label: 'Completed Orders',      value: '–' },
        { label: 'Cancelled Orders',      value: '–' },
        { label: 'Amount Refunded',       value: '–' },
    ]);
    const [ordersByStatus,  setOrdersByStatus]  = useState<StatusRow[]>([]);
    const [salesOverTime,   setSalesOverTime]   = useState<SalesPoint[]>([]);
    const [topProducts,     setTopProducts]     = useState<TopProductRow[]>([]);
    const [topCategories,   setTopCategories]   = useState<TopCategoryRow[]>([]);

    useEffect(() => { void fetchStores(); }, [fetchStores]);

    useEffect(() => {
        if (!selectedStoreId && stores.length === 1) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, selectedStoreId]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const computed = computeRange(dateRange);
            if (!computed) return;
            try {
                const res: AnalyticsResponse | null = await fetchAnalytics({
                    from: computed.from, to: computed.to, topLimit: 5,
                    storeId: selectedStoreId || undefined,
                });
                if (cancelled || !res) return;

                const getStatusCount = (s: string) => res.orders_by_status.find(r => r.status === s)?.count ?? 0;
                const completedCount = getStatusCount('DELIVERY_COMPLETE');
                const cancelledCount = getStatusCount('ORDER_CANCELLED');
                const convRate       = res.kpis.total_orders > 0 ? (completedCount / res.kpis.total_orders) * 100 : 0;

                setKpis([
                    { label: 'Total Orders',         value: res.kpis.total_orders.toLocaleString(),       delta: formatDelta(res.kpis.deltas?.total_orders) },
                    { label: 'Completed Orders',     value: completedCount.toLocaleString(),              delta: formatDelta(res.kpis.deltas?.completed_orders) },
                    { label: 'Cancelled Orders',     value: cancelledCount.toLocaleString(),              delta: formatDelta(res.kpis.deltas?.cancelled_orders) },
                    { label: 'Conversion Rate',      value: `${convRate.toFixed(1)}%`,                   delta: formatDelta(res.kpis.deltas?.conversion_rate) },
                    { label: 'Revenue',              value: formatCurrency(res.kpis.revenue),             delta: formatDelta(res.kpis.deltas?.revenue) },
                    { label: 'Product Revenue',      value: formatCurrency(res.kpis.product_revenue) },
                    { label: 'Delivery Fee Revenue', value: formatCurrency(res.kpis.delivery_fee_revenue) },
                    { label: 'Avg Order Value',      value: formatCurrency(res.kpis.avg_order_value),     delta: formatDelta(res.kpis.deltas?.avg_order_value) },
                    { label: 'Amount Refunded',      value: formatCurrency(res.kpis.wallet_refunds ?? 0), delta: formatDelta(res.kpis.deltas?.amount_refunded) },
                ]);
                setOrdersByStatus(res.orders_by_status.map(o => ({ label: o.status, value: o.count })));
                setSalesOverTime(res.sales_over_time);
                setTopProducts(res.top_products.map(p => ({
                    sku: p.sku || '—', name: p.name || 'Unnamed',
                    units_sold: p.units_sold, revenue: formatCurrency(p.revenue),
                    price_buckets: p.price_buckets,
                })));
                setTopCategories((res.top_categories ?? []).map(c => ({
                    category_id: c.category_id, name: c.name || 'Uncategorised',
                    units_sold: c.units_sold, revenue: formatCurrency(c.revenue),
                })));
            } catch {
                // error handled by useAnalytics hook
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [dateRange, selectedStoreId, fetchAnalytics]);

    const toggleRow = (i: number) => setExpandedRows(prev => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i);
        else next.add(i);
        return next;
    });

    const selectedStoreName = selectedStoreId
        ? stores.find(s => String(s.id) === String(selectedStoreId))?.name ?? 'Selected Store'
        : 'All Stores';

    const getDateRangeLabel = () => {
        if (!dateRange?.from) return 'No date selected';
        const fmt = (d: Date) => d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
        if (!dateRange.to || dateRange.from.getTime() === dateRange.to.getTime()) return fmt(dateRange.from);
        return `${fmt(dateRange.from)} - ${fmt(dateRange.to)}`;
    };

    const handleExport = () => {
        const fmt = (d: Date) => d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
        const dateText = dateRange?.from ? `${fmt(dateRange.from)}${dateRange.to ? ` - ${fmt(dateRange.to)}` : ''}` : 'All Time';
        const totalCount = ordersByStatus.reduce((s, r) => s + r.value, 0) || 1;
        const sections: string[] = [
            'ANALYTICS EXPORT',
            `Store:,${selectedStoreName}`,
            `Date Range:,${dateText}`,
            `Export Date:,${new Date().toLocaleString('en-ZA')}`,
            '',
            'KEY PERFORMANCE INDICATORS',
            'Metric,Value,Change vs Previous Period',
            ...kpis.map(k => `"${k.label}","${k.value.replace(/\s/g, '')}","${k.delta || 'N/A'}"`),
            '',
            'ORDERS BY STATUS',
            'Status,Count,Percentage',
            ...ordersByStatus.map(r => `"${r.label.replace(/_/g, ' ')}",${r.value},${((r.value / totalCount) * 100).toFixed(1)}%`),
            '',
            ...(salesOverTime.length > 0 ? [
                'COMPLETED SALES OVER TIME', 'Date,Revenue (ZAR)',
                ...salesOverTime.map(pt => `"${new Date(pt.date).toLocaleDateString('en-ZA')}","${Number(pt.revenue).toFixed(2)}"`),
                '',
            ] : []),
            'TOP PRODUCTS', 'SKU,Product Name,Units Sold,Revenue (ZAR)',
            ...topProducts.map(p => `"${p.sku}","${p.name.replace(/"/g, '""')}",${p.units_sold},"${p.revenue.replace(/\s/g, '')}"`),
        ];

        const blob = new Blob([sections.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = dateRange?.from
            ? `analytics-${dateRange.from.toISOString().split('T')[0]}${dateRange.to ? `-to-${dateRange.to.toISOString().split('T')[0]}` : ''}.csv`
            : 'analytics-export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const statusData      = ordersByStatus.length > 0 ? ordersByStatus : [{ label: 'awaiting_payment', value: 0 }, { label: 'packing', value: 0 }, { label: 'on_route', value: 0 }, { label: 'delivered', value: 0 }];
    const totalStatusCount = statusData.reduce((s, r) => s + r.value, 0) || 1;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-stone-900">Analytics</h1>
                    <p className="mt-1 text-sm text-stone-500">Store performance for {getDateRangeLabel()}</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Store picker */}
                    <div className="relative">
                        <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)}
                                className="block appearance-none border border-stone-300 rounded-lg bg-white pl-3 pr-8 py-2.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                            {stores.length > 1 && <option value="">All Stores</option>}
                            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-stone-400">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                    <DateRangePicker value={dateRange} onChange={setDateRange} />
                    <button type="button" onClick={handleExport}
                            className="rounded-lg bg-stone-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-stone-800 transition-all active:scale-95">
                        Export
                    </button>
                </div>
            </div>

            {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

            {/* KPIs */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {kpis.map(kpi => (
                    <StatCard key={kpi.label} label={kpi.label} value={loading ? '…' : kpi.value} delta={kpi.delta} />
                ))}
            </div>

            {/* Sales trend + Order statuses */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-stone-200 bg-white shadow-sm lg:col-span-2">
                    <div className="border-b border-stone-200 p-5">
                        <h2 className="text-sm font-semibold text-stone-900">Sales Trend</h2>
                        <p className="mt-1 text-xs text-stone-500">Revenue from completed orders</p>
                    </div>
                    <div className="p-5"><SalesChart data={salesOverTime} loading={loading} /></div>
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white shadow-sm">
                    <div className="border-b border-stone-200 p-5">
                        <h2 className="text-sm font-semibold text-stone-900">Order Statuses</h2>
                        <p className="mt-1 text-xs text-stone-500">Live distribution</p>
                    </div>
                    <div className="space-y-4 p-5">
                        {statusData.map(row => (
                            <div key={row.label}>
                                <div className="mb-1 flex items-center justify-between">
                                    <span className="text-xs capitalize text-stone-600">{row.label.replace(/_/g, ' ').toLowerCase()}</span>
                                    <span className="text-xs font-medium text-stone-900">{row.value}</span>
                                </div>
                                <div className="h-2 w-full overflow-hidden rounded-full bg-stone-100">
                                    <div className={`h-full transition-all duration-500 ${getStatusBarColor(row.label)}`}
                                         style={{ width: `${(row.value / totalStatusCount) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Top Products */}
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="border-b border-stone-200 p-5">
                    <h2 className="text-sm font-semibold text-stone-900">Top Products</h2>
                    <p className="mt-1 text-xs text-stone-500">Completed orders only · click a row to see price breakdown</p>
                </div>
                <div className="overflow-x-auto h-56">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">SKU</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Name</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Units</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Revenue</th>
                            <th className="w-8" />
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {topProducts.map((p, i) => {
                            const isExpanded      = expandedRows.has(i);
                            const hasMultiplePrices = p.price_buckets.length > 1;
                            return (
                                <React.Fragment key={`${p.sku}-${i}`}>
                                    <tr className={`bg-white transition-colors ${hasMultiplePrices ? 'cursor-pointer hover:bg-stone-50' : ''}`}
                                        onClick={() => hasMultiplePrices && toggleRow(i)}>
                                        <td className="px-4 py-3 font-medium text-stone-900">{p.sku}</td>
                                        <td className="px-4 py-3 text-stone-700">{p.name}</td>
                                        <td className="px-4 py-3 text-right font-semibold">{p.units_sold}</td>
                                        <td className="px-4 py-3 text-right font-semibold">{p.revenue}</td>
                                        <td className="pr-4 text-right">
                                            {hasMultiplePrices && (
                                                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </span>
                                            )}
                                        </td>
                                    </tr>
                                    {hasMultiplePrices && isExpanded && (
                                        <tr className="bg-stone-50">
                                            <td colSpan={5} className="pb-3 pt-0">
                                                <div className="space-y-2 px-4 pt-2">
                                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Price history</p>
                                                    {p.price_buckets.map((bucket, bi) => (
                                                        <div key={bi} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 ring-1 ring-stone-100">
                                                            <div className="flex items-center gap-2">
                                                                <span className="h-2 w-2 rounded-full bg-stone-300" />
                                                                <span className="text-xs font-medium text-stone-700">{formatCurrency(bucket.price)} <span className="font-normal text-stone-400">each</span></span>
                                                            </div>
                                                            <div className="flex items-center gap-4 text-xs">
                                                                <span className="text-stone-500">{bucket.units_sold} units</span>
                                                                <span className="font-medium text-stone-700">{formatCurrency(bucket.price * bucket.units_sold)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Top Categories */}
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="border-b border-stone-200 p-5">
                    <h2 className="text-sm font-semibold text-stone-900">Top Categories</h2>
                    <p className="mt-1 text-xs text-stone-500">Completed orders only · by revenue</p>
                </div>
                <div className="overflow-x-auto h-56">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Category</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Units</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Revenue</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {topCategories.length === 0 ? (
                            <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-stone-400">No category data for this period</td></tr>
                        ) : topCategories.map(c => (
                            <tr key={c.category_id} className="bg-white hover:bg-stone-50">
                                <td className="px-4 py-3 font-medium text-stone-900">{c.name}</td>
                                <td className="px-4 py-3 text-right font-semibold">{c.units_sold}</td>
                                <td className="px-4 py-3 text-right font-semibold">{c.revenue}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Analytics;